import { useState, useRef, useEffect, Fragment, useContext } from "react";
import {
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputBase,
  Box,
  IconButton,
  InputAdornment,
  FormControl,
  FormControlLabel,
  Collapse,
} from "@mui/material";
import {
  Zap,
  Pencil,
  Check,
  X,
  Plus,
  LockKeyhole,
  CalendarDays,
  Clock,
} from "lucide-react";
import FlexBox from "components/common/FlexBox";
import TextField from "components/common/TextField";
import Select from "components/common/Select";
import Checkbox from "components/common/Checkbox";
import { DelegationOpportunity } from "components/hooks/useTaskAgentState";
import { useGetTask } from "services/tasks";
import { useListMemberSecrets, useListSecrets } from "services/secrets";
import {
  ListMemberSecretsRequest,
  ListSecretsRequest,
} from "protogen/secrets_service_pb";
import { GetTaskRequest } from "protogen/tasks_service_pb";
import { CurrentUserContext } from "components/context/RequireAuth";
import { isUserAdvisor } from "common/userUtils";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DesktopTimePicker } from "@mui/x-date-pickers/DesktopTimePicker";
import useIsMobile from "components/hooks/useIsMobile";

export interface DelegationDialogData {
  description: string;
  details: Array<{
    name: string;
    value?: string;
    detailType?: string;
    entityType?: string;
  }>;
  actionDateSec?: number;
  timeEstimateMinutes?: number;
}

interface DelegationConfirmDialogProps {
  open: boolean;
  task: DelegationOpportunity;
  taskRef?: string;
  familyRef?: string;
  onClose: () => void;
  onConfirm: (data: DelegationDialogData) => void;
  isLoading: boolean;
}

export default function DelegationConfirmDialog({
  open,
  task,
  taskRef: _taskRef,
  familyRef: _familyRef,
  onClose,
  onConfirm,
  isLoading,
}: DelegationConfirmDialogProps) {
  const isMobile = useIsMobile();
  const [editingFields, setEditingFields] = useState<Set<number>>(new Set());
  const [deletedFields, setDeletedFields] = useState<Set<number>>(new Set());
  const [editedValues, setEditedValues] = useState<Record<number, string>>({});
  const [secretRows, setSecretRows] = useState<
    Array<{ id: string; selectedSecretRef: string | null }>
  >([]);
  const descriptionRef = useRef<HTMLInputElement>(null);
  const [hasSchedule, setHasSchedule] = useState(false);
  const [scheduledDate, setScheduledDate] = useState<Date | null>(null);
  const [scheduledTime, setScheduledTime] = useState<Date | null>(null);
  const [timeEstimate, setTimeEstimate] = useState<number | undefined>(
    undefined,
  );
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [timePickerOpen, setTimePickerOpen] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);

  const pendingAddSecret = useRef(false);

  const currentUser = useContext(CurrentUserContext);
  const isAdvisor = isUserAdvisor(currentUser);
  const { request: getTask, data: taskData } = useGetTask();
  const { request: listAdvisorSecrets, data: advisorSecretsData } =
    useListSecrets();
  const { request: listMemberSecrets, data: memberSecretsData } =
    useListMemberSecrets();
  const familySecrets = isAdvisor
    ? advisorSecretsData?.secrets
    : memberSecretsData?.secrets;

  const hasExistingSecrets = task.details.some(
    (d) => d.detailType === "grant" && d.entityType === "secret",
  );

  useEffect(() => {
    if (open) {
      setEditingFields(new Set());
      setDeletedFields(new Set());
      setEditedValues({});
      setSecretRows([]);
      setScheduleError(null);
      setTimeEstimate(task.timeEstimateMinutes);

      // Initialize schedule from task.actionDateSec
      if (task.actionDateSec && task.actionDateSec > 0) {
        const actionDate = new Date(task.actionDateSec * 1000);
        setHasSchedule(true);
        setScheduledDate(actionDate);
        setScheduledTime(actionDate);
      } else {
        setHasSchedule(false);
        setScheduledDate(null);
        setScheduledTime(null);
      }

      if (_taskRef) {
        const req = new GetTaskRequest();
        req.taskRef = _taskRef;
        getTask(req);
      }
    }
  }, [open, _taskRef, task.actionDateSec]);

  const loadSecrets = () => {
    if (familySecrets) return;
    const effectiveFamilyRef = taskData?.task?.familyRef || _familyRef;
    if (isAdvisor) {
      if (!effectiveFamilyRef) return;
      const req = new ListSecretsRequest();
      req.familyRef = effectiveFamilyRef;
      listAdvisorSecrets(req);
    } else {
      listMemberSecrets(new ListMemberSecretsRequest());
    }
  };

  // Load secrets eagerly only if the task already has secret details
  useEffect(() => {
    if (open && hasExistingSecrets) {
      loadSecrets();
    }
  }, [open, taskData?.task?.familyRef, _familyRef, hasExistingSecrets]);

  // Parse existing secrets from task.details after secretsData has loaded
  useEffect(() => {
    if (open && familySecrets) {
      const existingSecrets = task.details
        .filter((d) => d.detailType === "grant" && d.entityType === "secret")
        .map((d) => ({
          id: Math.random().toString(),
          selectedSecretRef: d.value || null,
        }));
      setSecretRows(existingSecrets);

      if (pendingAddSecret.current) {
        pendingAddSecret.current = false;
        addSecretRow();
      }
    }
  }, [open, familySecrets, task.details]);

  const addSecretRow = () => {
    setSecretRows((prev) => [
      ...prev,
      {
        id: Math.random().toString(),
        selectedSecretRef: null,
      },
    ]);
  };

  const updateSecretRow = (rowId: string, secretRef: string | null) => {
    setSecretRows((prev) =>
      prev.map((row) =>
        row.id === rowId ? { ...row, selectedSecretRef: secretRef } : row,
      ),
    );
  };

  const removeSecretRow = (rowId: string) => {
    setSecretRows((prev) => prev.filter((row) => row.id !== rowId));
  };

  const toggleEdit = (index: number) => {
    setEditingFields((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const deleteField = (index: number) => {
    setDeletedFields((prev) => new Set(prev).add(index));
  };

  const handleConfirm = () => {
    // Validate schedule if set
    let actionDateSec: number | undefined = undefined;
    if (hasSchedule) {
      if (!scheduledDate || !scheduledTime) {
        setScheduleError("Both date and time are required when scheduling");
        return;
      }
      const combinedDateTime = new Date(scheduledDate);
      combinedDateTime.setHours(scheduledTime.getHours());
      combinedDateTime.setMinutes(scheduledTime.getMinutes());
      combinedDateTime.setSeconds(0);
      combinedDateTime.setMilliseconds(0);
      actionDateSec = Math.floor(combinedDateTime.getTime() / 1000);
    }

    const editedDetails = task.details
      .map((detail, index) => {
        if (deletedFields.has(index)) return null;
        return {
          name: detail.name,
          value: editedValues[index] ?? detail.value,
        };
      })
      .filter(
        (detail): detail is { name: string; value: string } => detail !== null,
      );

    const secretDetails = secretRows
      .filter((row) => row.selectedSecretRef)
      .map((row) => {
        const secret = familySecrets?.find(
          (s) => s.ref === row.selectedSecretRef,
        );
        return {
          name: secret?.name || "Secret",
          value: row.selectedSecretRef!,
          detailType: "grant",
          entityType: "secret",
        };
      });

    onConfirm({
      description: descriptionRef.current?.value ?? task.description,
      details: [...editedDetails, ...secretDetails],
      actionDateSec,
      timeEstimateMinutes: timeEstimate,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen
      sx={{
        "& .MuiDialog-container": {
          alignItems: { xs: "flex-end", sm: "center" },
        },
        "& .MuiDialog-paper": {
          margin: { xs: 0, sm: "32px" },
          maxHeight: { xs: "90vh", sm: "calc(100% - 64px)" },
          borderRadius: { xs: "16px 16px 0 0", sm: "8px" },
          width: "100%",
          maxWidth: { sm: "600px" },
        },
      }}
    >
      <DialogTitle>Lightning Task: {task.title}</DialogTitle>
      <DialogContent sx={{ padding: { xs: "8px 16px", sm: "20px 24px" } }}>
        <FlexBox direction="column" gap="20px" sx={{ paddingTop: "8px" }}>
          <TextField
            multiline
            minRows={3}
            defaultValue={task.description}
            label="Instructions"
            fullWidth
            inputRef={descriptionRef}
          />

          <TextField
            type="number"
            label="Estimated time (min)"
            value={timeEstimate ?? ""}
            onChange={(e) => {
              const val = e.target.value;
              setTimeEstimate(val === "" ? undefined : parseInt(val, 10));
            }}
            sx={{
              width: { xs: "100%", sm: "180px" },
              flexShrink: 0,
              ".MuiInputBase-root": { minHeight: "50px" },
              "input::-webkit-outer-spin-button, input::-webkit-inner-spin-button":
                {
                  WebkitAppearance: "none",
                  margin: 0,
                },
              'input[type="number"]': {
                MozAppearance: "textfield",
              },
            }}
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "auto 1fr" },
              rowGap: isMobile ? "0" : "12px",
              columnGap: "16px",
              alignItems: "start",
            }}
          >
            {task.details.map((detail, index) => {
              if (deletedFields.has(index)) return null;
              // Skip secrets - they're handled separately
              if (
                detail.detailType === "grant" &&
                detail.entityType === "secret"
              ) {
                return null;
              }
              return (
                <Fragment key={index}>
                  <Typography
                    variant="bodySmallHeavy"
                    sx={{
                      ":not(:first-of-type)": {
                        paddingTop: isMobile ? "6px" : "0",
                      },
                    }}
                  >
                    {detail.name}
                  </Typography>
                  <FlexBox alignItems="center" gap="8px">
                    {editingFields.has(index) ? (
                      <>
                        <InputBase
                          value={editedValues[index] ?? detail.value}
                          onChange={(e) =>
                            setEditedValues((prev) => ({
                              ...prev,
                              [index]: e.target.value,
                            }))
                          }
                          autoFocus
                          sx={{
                            fontSize: "14px",
                            fontWeight: 500,
                            padding: "4px 8px",
                            border: "1px solid #D4D4D4",
                            borderRadius: "4px",
                            flex: 1,
                          }}
                        />
                        <Check
                          size={18}
                          color="#198282"
                          style={{ cursor: "pointer", flexShrink: 0 }}
                          onClick={() => toggleEdit(index)}
                        />
                      </>
                    ) : (
                      <>
                        <Typography
                          sx={{ flex: 1, wordBreak: "break-word", minWidth: 0 }}
                          variant="bodySmall"
                        >
                          {editedValues[index] ?? detail.value}
                        </Typography>
                        <Pencil
                          size={16}
                          color="#6B6E7B"
                          style={{ cursor: "pointer", flexShrink: 0 }}
                          onClick={() => toggleEdit(index)}
                        />
                        <X
                          size={16}
                          color="#6B6E7B"
                          style={{ cursor: "pointer", flexShrink: 0 }}
                          onClick={() => deleteField(index)}
                        />
                      </>
                    )}
                  </FlexBox>
                </Fragment>
              );
            })}
          </Box>

          <FlexBox direction="column" gap="8px">
            <FlexBox alignItems="center" justifyContent="space-between">
              <Typography variant="bodyHeavy">Secrets</Typography>
              <Button
                variant="text"
                startIcon={<Plus size={16} />}
                onClick={() => {
                  if (familySecrets) {
                    addSecretRow();
                  } else {
                    pendingAddSecret.current = true;
                    loadSecrets();
                  }
                }}
                sx={{ height: "unset" }}
              >
                Add
              </Button>
            </FlexBox>
            <Typography variant="bodySmall" sx={{ color: "#6B6E7B" }}>
              Provide short term access to individual secrets required for the
              completion of the task.
            </Typography>
            {secretRows.length === 0 && (
              <Typography
                variant="bodySmall"
                sx={{ color: "#B3AFB6", fontStyle: "italic" }}
              >
                No secrets provided
              </Typography>
            )}
            {secretRows.map((row) => (
              <FlexBox key={row.id} alignItems="center" gap="8px">
                {!row.selectedSecretRef ? (
                  familySecrets && familySecrets.length > 0 ? (
                    <>
                      <Select
                        placeholder="Select a secret"
                        value={row.selectedSecretRef ?? ""}
                        onChange={(e) => {
                          const value = e.target.value as string;
                          updateSecretRow(row.id, value === "" ? null : value);
                        }}
                        options={[
                          { label: "Select a secret", value: "" },
                          ...familySecrets.map((secret) => ({
                            label: secret.name,
                            value: secret.ref,
                          })),
                        ]}
                        variant="detachedOutlined"
                        size="small"
                        sx={{ flex: 1, minWidth: 200 }}
                      />
                      <X
                        size={18}
                        color="#6B6E7B"
                        style={{ cursor: "pointer" }}
                        onClick={() => removeSecretRow(row.id)}
                      />
                    </>
                  ) : (
                    <>
                      <Typography
                        variant="bodySmall"
                        sx={{ color: "#B3AFB6", fontStyle: "italic", flex: 1 }}
                      >
                        {familySecrets ? "No secrets available" : "Loading..."}
                      </Typography>
                      <X
                        size={18}
                        color="#6B6E7B"
                        style={{ cursor: "pointer" }}
                        onClick={() => removeSecretRow(row.id)}
                      />
                    </>
                  )
                ) : (
                  <FlexBox alignItems="center" gap="8px" sx={{ flex: 1 }}>
                    <FlexBox alignItems="center" gap="8px">
                      <LockKeyhole size={16} />
                      <Typography
                        variant="bodySmall"
                        sx={{ flex: 1, minWidth: 0 }}
                      >
                        {familySecrets?.find(
                          (s) => s.ref === row.selectedSecretRef,
                        )?.name || "Secret"}
                      </Typography>
                    </FlexBox>
                    <X
                      size={16}
                      color="#6B6E7B"
                      style={{ cursor: "pointer" }}
                      onClick={() => removeSecretRow(row.id)}
                    />
                  </FlexBox>
                )}
              </FlexBox>
            ))}
          </FlexBox>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              padding: "12px 16px",
              border: "1px solid #D4D4D4",
              borderRadius: "8px",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasSchedule}
                  onChange={(e) => {
                    setHasSchedule(e.target.checked);
                    if (!e.target.checked) {
                      setScheduledDate(null);
                      setScheduledTime(null);
                      setScheduleError(null);
                    }
                  }}
                  size="small"
                />
              }
              label={
                <Typography variant="bodySmall">Schedule for later</Typography>
              }
            />
            <Collapse in={hasSchedule}>
              <Typography
                variant="bodySmall"
                sx={{ color: "#6B6E7B", marginBottom: "8px" }}
              >
                This task will not be made available for an advisor to pick up
                until the scheduled time.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: "12px",
                  flexWrap: "wrap",
                  ".MuiInputBase-root": {
                    minHeight: "48px",
                  },
                }}
              >
                <FormControl sx={{ flex: 1, minWidth: "140px" }}>
                  <DatePicker
                    value={scheduledDate}
                    sx={{
                      width: "100%",
                      ".MuiButtonBase-root": {
                        marginRight: "unset",
                      },
                      input: {
                        marginTop: "0",
                      },
                    }}
                    onChange={(newValue) => {
                      setScheduledDate(newValue);
                      setScheduleError(null);
                    }}
                    format="MM/dd/yyyy"
                    open={datePickerOpen}
                    onClose={() => setDatePickerOpen(false)}
                    slotProps={{
                      textField: {
                        size: "small",
                        onClick: (e) => {
                          e.stopPropagation();
                          setDatePickerOpen(true);
                          setTimeout(() => {
                            (e.target as HTMLInputElement).focus();
                          });
                        },
                        InputProps: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setDatePickerOpen(true)}
                                edge="end"
                              >
                                <CalendarDays size={20} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      },
                    }}
                  />
                </FormControl>
                <DesktopTimePicker
                  value={scheduledTime}
                  sx={{
                    flex: 1,
                    minWidth: "120px",
                    ".MuiButtonBase-root": { marginRight: "unset" },
                    "& .MuiInputBase-input": { marginTop: "0px" },
                  }}
                  onChange={(newValue) => {
                    setScheduledTime(newValue);
                    setScheduleError(null);
                  }}
                  open={timePickerOpen}
                  onClose={() => setTimePickerOpen(false)}
                  slotProps={{
                    textField: {
                      size: "small",
                      onClick: (e) => {
                        e.stopPropagation();
                        setTimePickerOpen(!timePickerOpen);
                        setTimeout(() => {
                          (e.target as HTMLInputElement).focus();
                        });
                      },
                      InputProps: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setTimePickerOpen(!timePickerOpen)}
                              edge="end"
                            >
                              <Clock size={20} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    },
                  }}
                />
              </Box>
            </Collapse>
            {scheduleError && (
              <Typography variant="bodySmall" sx={{ color: "error.main" }}>
                {scheduleError}
              </Typography>
            )}
          </Box>
        </FlexBox>
      </DialogContent>
      <DialogActions sx={{ padding: "16px 24px" }}>
        <Button variant="outlined" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isLoading}
          startIcon={
            isLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <Zap size={16} fill="currentColor" />
            )
          }
        >
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
}
